import {
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RestAPI } from "@aws-amplify/api-rest";
import { icon } from "@fortawesome/fontawesome-svg-core";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

import { toast } from "react-toastify";
import { FarmContext } from "../../contexts/FarmContext";
import injectScript from "../../utils/inject";

import {
    AfterNodesAndSectorsEffect,
    SectorPolygon,
    UnasignedPolygon,
} from "./SectorPolygon";
import { NodeMarker, UnasignedMarker } from "./NodeMarker";
import { DrawingManager } from "./DrawingManager";
import { FarmMarker } from "./FarmMarker";
import getBaseLangs from "../../utils/language";
import withName from "../../hoc/withName";
import TagManager from "react-gtm-module";

const [linkIcon] = icon(faExternalLinkAlt).html;

const changeDistanceAndZoom = (values) => {
    const boundSouthWest = values.bounds.southWest;
    const boundNorthEast = values.bounds.northEast;
    const boundNorthWest = {
        lat: boundNorthEast.lat,
        lng: boundSouthWest.lng,
    };
    // distance, newBound and position
    const distanceInMeterHoriz = window.google.maps.geometry.spherical.computeDistanceBetween(boundNorthWest, boundNorthEast);
    const increaseInMeterHoriz = distanceInMeterHoriz * 0.51;
    const newNorthEast = window.google.maps.geometry.spherical.computeOffset(boundNorthEast, increaseInMeterHoriz, 90).toJSON();

    const distanceInMeterVert = window.google.maps.geometry.spherical.computeDistanceBetween(boundNorthWest, boundSouthWest);
    const increaseInMeterVert = distanceInMeterVert * 0.07;
    const newSouthWest = window.google.maps.geometry.spherical.computeOffset(boundSouthWest, increaseInMeterVert, 180).toJSON();

    // zoomLevel
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 17;
    const latRad = (lat) => {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };
    const zoom = (mapPx, worldPx, fraction) => {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    };
    const latFraction = (latRad(newNorthEast.lat) - latRad(newSouthWest.lat)) / Math.PI;
    const lngDiff = newNorthEast.lng - newSouthWest.lng;
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
    const latZoom = zoom(471.88 * 0.99, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(764 * 0.99, WORLD_DIM.width, lngFraction);

    const position = values.position;
    const getZoom = Math.min(latZoom, lngZoom, ZOOM_MAX);
    const boundPositionZoom = { newNorthEast, position, getZoom, newSouthWest };

    return boundPositionZoom;
};

const updateMapInfo = async (farmId, values, t, dispatch, mapNotVisible) => {
    if (values.isModified) {
        const distanceAndZoom = changeDistanceAndZoom(values);
        values.bounds = { southWest: distanceAndZoom.newSouthWest, northEast: distanceAndZoom.newNorthEast };
        values.position = distanceAndZoom.position;
        values.zoom = distanceAndZoom.getZoom;

        delete values.isModified;
        try {
            const data = await RestAPI.post(
                "apiAdminDropControl",
                `/farms/${farmId}/map`,
                { body: values }
            );
            if (data && data.mapa_json) {
                const mapa_json = JSON.parse(data.mapa_json);
                // recreate the "original" object so its re-formated
                dispatch({
                    type: "set",
                    params: (ds) => ({
                        ...ds,
                        dataFarm: { ...ds.dataFarm, mapa_json },
                        original: { ...ds.original },
                        formated: false,
                    }),
                });
            }
            if (t) {
                toast.success(t("forms.toastSuccess"), {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 6000,
                });
                if (mapNotVisible)
                    toast.warning(t("maps.mapaNoVisibleTip"), {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: 6000,
                    });
                TagManager.dataLayer({
                    dataLayer: {
                        event: "edicion",
                        edit_type: "mapa",
                        campo_id: farmId,
                        page_path: window.location.pathname,
                        page_search: window.location.pathname + window.location.search,
                        cuenta_id: null,
                        user_email: null,
                    },
                });

                return true;
            } else return true;
        } catch (err) {
            console.error(err);
            if (t) {
                toast.error(t("forms.toastError"), {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 6000,
                });
                return false;
            } else return false;
        }
    } else {
        t &&
      toast.info(t("forms.toastInfo"), {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
      });
    }
};

const isDiffLatLng = (point1, point2) => {
    if (!point1) return Boolean(point2);
    return !point2 || point1.lat !== point2.lat || point1.lng !== point2.lng;
};

const isDiffPath = (path1, path2) => {
    if (!path1) return Boolean(path2);
    return (
        !path2 ||
    path1.length !== path2.length ||
    path1.some((point1, idx) => isDiffLatLng(point1, path2[idx]))
    );
};

const setMapCenter = ({ lat, lng }, map) => {
    const center = new window.google.maps.LatLng(lat, lng);
    map.setCenter(center);
};

const getInfo = ({ nodeId, sectorId }, data, drawedElements, nodes, sectors, map, forcedPosition ) => {
    const res = { isModified: false };
    const bounds = new window.google.maps.LatLngBounds();
    const { nodos: dataNodos, sectores: dataSectores } = data || {};

    for (const nodo in dataNodos) {
        if (nodo !== nodeId) {
            bounds.extend(new window.google.maps.LatLng(dataNodos[nodo].lat, dataNodos[nodo].lng));
        }
    }
    if (nodeId) {
        let position = null;
        res.nodos = {};
        if (forcedPosition) {
            if ( typeof forcedPosition.lat === "number" || typeof forcedPosition.lng === "number") {
                position = new window.google.maps.LatLng(forcedPosition.lat,  forcedPosition.lng);
            }
        } else if (drawedElements.length) {
            position = drawedElements[0].getPosition();
        } else if (nodes[nodeId]) {
            position = nodes[nodeId].getPosition();
        }
        res.nodos[nodeId] = position && position.toJSON();
        if (position) {
            bounds.extend(position);
        }
        if (isDiffLatLng(res.nodos[nodeId], dataNodos && dataNodos[nodeId])) {
            res.isModified = true;
        } else if (!sectorId) return res;
    }

    for (const sector in sectors) {
        if (sector !== sectorId) {
            sectors[sector].getPath().forEach((point) => bounds.extend(point));
        }
    }
    if (sectorId) {
        let path,
            array = null;
        const sectorBounds = new window.google.maps.LatLngBounds();
        res.sectores = {};

        if (drawedElements.length) {
            if (drawedElements[0].getPath) {
                path = drawedElements[0].getPath();
                array = new Array(path.getLength());
                path.forEach((point, index) => {
                    sectorBounds.extend(point);
                    array[index] = point.toJSON();
                });
            } else if (drawedElements[0].getRadius) {
                const center = drawedElements[0].getCenter().toJSON();
                const radius = drawedElements[0].getRadius();
                const circlePath = (center, radius, points) => {
                    const a = [], p = 360 / points;
                    let d = 0;
                    for (let i = 0; i < points; ++i, d += p) {
                        const point = window.google.maps.geometry.spherical.computeOffset(center,radius,d);
                        a.push(point);
                        sectorBounds.extend(point);
                    }
                    return a;
                };
                const points = 32;
                array = circlePath(center, radius, points);
            } else {
                path = drawedElements[0].getBounds();
                sectorBounds.union(path);
                path = path.toJSON();
                array = [
                    { lat: path.north, lng: path.east },
                    { lat: path.north, lng: path.west },
                    { lat: path.south, lng: path.west },
                    { lat: path.south, lng: path.east },
                ];
            }
        } else if ((path = sectors[sectorId])) {
            path = path.getPath();
            array = new Array(path.getLength());
            path.forEach((point, index) => {
                sectorBounds.extend(point);
                array[index] = point.toJSON();
            });
        }
        if (isDiffPath(array, dataSectores && dataSectores[sectorId] && dataSectores[sectorId].path)) {
            res.isModified = true;
        }
        if (array) {
            bounds.union(sectorBounds);
            res.sectores[sectorId] = {
                path: array,
                bounds: { southWest: sectorBounds.getSouthWest().toJSON(), northEast: sectorBounds.getNorthEast().toJSON() }
            };
        } else {
            res.sectores[sectorId] = null;
        }
    }
    res.zoom = map && map.getZoom();
    if (res.isModified || sectorId) {
        const { lat, lng } = (!bounds.isEmpty() && bounds.toSpan().toJSON()) || {};
        if (Math.min(lat || 0, lng || 0) < 0.00001) map && bounds.union(map.getBounds());

        res.bounds = { southWest: bounds.getSouthWest().toJSON(), northEast: bounds.getNorthEast().toJSON() };
        res.position = bounds.getCenter().toJSON();
    }

    if ((!res.zoom) || (res.zoom > 17)) res.zoom = 17;
    return res;
};

const GoogleMapsFarm = ({
    apiKey,
    accountId,
    data,
    edit,
    farmId,
    filterNodes,
    filterSectors,
    hideNodes,
    hideSectors,
    innerRef,
    language,
    nodeId,
    updateSectorArea,
    onLoadFailed,
    husoHorario,
    fechaUltimoDato,
    iconBorder,
    alternateView,
    unasigned,
    notShowPolygons,
    notShowPoints,
    isImageAligner,
    setFinalBounds,
    imageInfo,
}) => {
    const { t, i18n } = useTranslation();
    const [hover, setHover] = useState({});
    const [initDone, setInit] = useState(false);
    const [_, dispatch] = useContext(FarmContext);
    const drawedElements = useRef([]);
    const infowindow = useRef(null);
    const gmap = useRef(null);
    const nodes = useRef({});
    const sectors = useRef({});

    const sectorsDict = dispatch({ type: "get", path: "sectorsDict" });
    const nodesDict = dispatch({ type: "get", path: "nodesDict" });
    const hidrasDict = dispatch({ type: "get", path: "hidrasDict" });
    const navigate = useNavigate();

    const openInfoWindow = (map, content, latLng, onClickFn) => {
        const template = document.createElement("template");
        template.innerHTML = content.trim(); // Never return a text node of whitespace as the result
        const clickListener = template.content.querySelector("#clickListener");
        if (clickListener && onClickFn) clickListener.onclick = onClickFn;

        if (infowindow.current) {
            infowindow.current.setOptions({
                content: template.content.firstChild,
                position: latLng,
            });
        } else {
            infowindow.current = new window.google.maps.InfoWindow({
                content: template.content.firstChild,
                position: latLng,
            });
        }
        infowindow.current.open(map);
    };

    const closeInfoWindow = () => {
        if (infowindow.current) {
            infowindow.current.setMap(null);
        }
    };

    const childrenCommon = useMemo(() => {
        if (!initDone || !gmap.current) return null;

        return {
            alternateView: alternateView,
            bounds: new window.google.maps.LatLngBounds(),
            data,
            drawedElements,
            farmId,
            accountId,
            linkIcon,
            openInfoWindow,
            closeInfoWindow,
            gmap: gmap.current,
        };
    }, [data, farmId, accountId, initDone, alternateView]);

    if (childrenCommon) {
    // we provide updated values for this, but do not re-render because some of them changes
        const area_sufix = (sectorsDict && sectorsDict.area_sufix) || "";
        if (childrenCommon.area_sufix !== area_sufix) {
            childrenCommon.area_sufix = area_sufix;
        }
        if (childrenCommon.navigate !== navigate) {
            childrenCommon.navigate = navigate;
        }
        if (childrenCommon.edit !== edit) {
            childrenCommon.edit = edit;
        }
        const lngs = getBaseLangs(i18n);
        if (!childrenCommon.lngs || childrenCommon.lngs[0] !== lngs[0]) {
            childrenCommon.lngs = lngs;
        }
        if (childrenCommon.t !== t) {
            childrenCommon.t = t;
        }
    }

    const sectorsCommon = useMemo(() => {
        if (accountId || !sectorsDict) return null;
        const area_sufix = (sectorsDict && sectorsDict.area_sufix) || "";

        return { area_sufix, sectorsDict, hidrasDict, sectors };
    }, [accountId, sectorsDict, hidrasDict]);

    if (sectorsCommon) {
    // we provide updated values for this, but do not re-render because some of them changes
        if (sectorsCommon.updateSectorArea !== updateSectorArea) {
            sectorsCommon.updateSectorArea = updateSectorArea;
        }
    }

    const nodesCommon = useMemo(
        () => (accountId || !nodesDict ? null : { nodesDict, nodes }),
        [accountId, nodesDict]
    );

    useImperativeHandle(
        innerRef,
        () => ({
            deleteElement: () => {
                const index = 0;
                const polygon = drawedElements.current[index];

                if (polygon) {
                    polygon.setMap(null);
                    drawedElements.current.splice(index, 1);
                } else {
                    toast.error(t("forms.toastError"), {
                        position: toast.POSITION.TOP_RIGHT,
                        autoClose: 6000,
                    });
                }
            },
            getMapInfo: (params, forcedPosition) =>
                getInfo(
                    params,
                    data,
                    drawedElements.current,
                    nodes.current,
                    sectors.current,
                    gmap.current,
                    forcedPosition
                ),
            setSelected: (uid) => {
                const objeto_id = uid && uid.split("_")[0];
                const [tipo, id] = (objeto_id && objeto_id.split("-")) || [];
                setHover(
                    tipo === "nodo"
                        ? { nodo: id }
                        : tipo === "sector"
                            ? { sector: id }
                            : tipo === "unasigned"
                                ? { unasigned: id }
                                : {}
                );
            },
            setCenter: (params) => setMapCenter(params, gmap.current),
        }),
        [data]
    );

    let alignerOverlay;

    const init = () => {
        if (!window.google)
            throw new Error("[google-maps-farm]: Google script not loaded");
        if (!window.google.maps)
            throw new Error("[google-maps-farm]: Google maps script not loaded");
        const mapDiv = document.getElementById("google_maps_div");
        if (!mapDiv) throw new Error("[google-maps-farm]: No Google Maps div");

        let center = { lat: 0, lng: -90 };
        let zoom = 2;

        if (data && data.position) {
            center = data.position;
            zoom = data.zoom || 15;
        } else if (
            data &&
      data.bounds &&
      data.bounds.southWest &&
      data.bounds.northEast
        ) {
            const northEast = new window.google.maps.LatLng(
                data.bounds.northEast.lat,
                data.bounds.northEast.lng
            );
            const southWest = new window.google.maps.LatLng(
                data.bounds.southWest.lat,
                data.bounds.southWest.lng
            );
            center = new window.google.maps.LatLngBounds(
                southWest,
                northEast
            ).getCenter();
            zoom = data.zoom || 15;
        }

        gmap.current = new window.google.maps.Map(mapDiv, {
            disableDoubleClickZoom: true,
            mapTypeControl: false,
            center,
            zoom,
            mapTypeId: "hybrid",
            styles: [
                {
                    featureType: "administrative",
                    elementType: "geometry",
                    stylers: [{ visibility: "off" }],
                },
                {
                    featureType: "poi",
                    stylers: [{ visibility: "off" }],
                },
                {
                    featureType: "road",
                    elementType: "labels.icon",
                    stylers: [{ visibility: "off" }],
                },
                {
                    featureType: "transit",
                    stylers: [{ visibility: "off" }],
                },
            ],
        });

        class AlignerOverlay extends window.google.maps.OverlayView {
            bounds_;
            image_;
            div_;
            map_;
            constructor(bounds, image, map) {
                super();
                this.bounds_ = bounds;
                this.image_ = image;
                this.div_ = null;
                this.map_ = map;
                this.setMap(map);
                this.set("dragging", false);

                this.addListener("dragging_changed", function () {
                    const dragging = this.get("dragging");
                    map.setOptions({ draggable: !dragging });
                });
            }

            onAdd() {
                this.div_ = document.createElement("div");
                this.div_.style.borderStyle = "none";
                this.div_.style.borderWidth = "0px";
                this.div_.style.position = "absolute";
                this.div_.draggable = true;
                this.div_.style.border = "1px dashed red";
                const self = this;
                const panes = this.getPanes();

                // Create the img element and attach it to the div.
                const img = document.createElement("img");

                img.src = this.image_;
                img.style.width = "100%";
                img.style.height = "100%";
                img.style.position = "absolute";
                img.style.opacity = "0.5";

                img.id = "wiseImgOverlay";
                this.div_.appendChild(img);

                panes.overlayMouseTarget.appendChild(this.div_);

                let selecting = false;

                window.onkeydown = function (e) {
                    selecting = e.keyIdentifier == "Shift" || e.shiftKey == true;
                };
                window.onkeyup = function (e) {
                    selecting = false;
                };

                window.google.maps.event.addDomListener(
                    this.map.getDiv(),
                    "mouseleave",
                    function () {
                        window.google.maps.event.trigger(this.div_, "mouseup");
                    }
                );

                window.google.maps.event.addDomListener(
                    this.div_,
                    "mousedown",
                    function (e) {
                        if (!selecting) return;
                        this.style.cursor = "move";
                        self.map.set("draggable", false);
                        self.set("origin", e);
                        //console.log("mousedown");

                        self.moveHandler = window.google.maps.event.addDomListener(
                            self.map.getDiv(),
                            "mousemove",
                            function (e) {
                                //console.log("mousemove")
                                if (!selecting) return;

                                const origin = self.get("origin");
                                const left = origin.clientX - e.clientX;
                                const top = origin.clientY - e.clientY;

                                //console.log([left, top]);

                                const swPix = self
                                    .getProjection()
                                    .fromLatLngToDivPixel(self.bounds_.getSouthWest());
                                const nePix = self
                                    .getProjection()
                                    .fromLatLngToDivPixel(self.bounds_.getNorthEast());

                                //console.log([[swPix.lat], nePix])

                                const sw = self
                                    .getProjection()
                                    .fromDivPixelToLatLng(
                                        new window.google.maps.Point(swPix.x - left, swPix.y - top)
                                    );
                                const ne = self
                                    .getProjection()
                                    .fromDivPixelToLatLng(
                                        new window.google.maps.Point(nePix.x - left, nePix.y - top)
                                    );

                                //console.log([sw.lat(), sw.lng()])
                                //console.log([ne.lat(), ne.lng()])

                                self.set("origin", e);
                                self.updateBounds(new window.google.maps.LatLngBounds(sw, ne));
                            }
                        );
                    }
                );

                window.google.maps.event.addDomListener(
                    this.div_,
                    "mouseup",
                    function (e) {
                        this.style.cursor = "default";
                        self.map.set("draggable", true);
                        //console.log("mouseup");
                        window.google.maps.event.removeListener(self.moveHandler);
                    }
                );
            }

            draw() {
                // We use the south-west and north-east
                // coordinates of the overlay to peg it to the correct position and size.
                // To do this, we need to retrieve the projection from the overlay.
                const overlayProjection = this.getProjection();
                // Retrieve the south-west and north-east coordinates of this overlay
                // in LatLngs and convert them to pixel coordinates.
                // We'll use these coordinates to resize the div.
                const sw = overlayProjection.fromLatLngToDivPixel(
                    this.bounds_.getSouthWest()
                );
                const ne = overlayProjection.fromLatLngToDivPixel(
                    this.bounds_.getNorthEast()
                );

                // Resize the image's div to fit the indicated dimensions.
                if (this.div_) {
                    this.div_.style.left = sw.x + "px";
                    this.div_.style.top = ne.y + "px";
                    this.div_.style.width = ne.x - sw.x + "px";
                    this.div_.style.height = sw.y - ne.y + "px";
                }
            }

            onRemove() {
                if (this.div_) {
                    this.div_.parentNode.removeChild(this.div_);
                    delete this.div_;
                }
            }

            updateBounds(bounds) {
                this.bounds_ = bounds;
                setFinalBounds(bounds);
                this.draw();
            }

            updateOpacity(value) {
                if (this.div_) {
                    const img = this.div_.firstChild;
                    img.style.opacity = value;
                }
            }
        }

        if (isImageAligner && imageInfo) {
            const sw = new window.google.maps.LatLng(
                imageInfo.bounds.south,
                imageInfo.bounds.west
            );
            const ne = new window.google.maps.LatLng(
                imageInfo.bounds.north,
                imageInfo.bounds.east
            );

            const bounds = new window.google.maps.LatLngBounds(sw, ne);
            alignerOverlay = new AlignerOverlay(
                bounds,
                imageInfo.image,
                gmap.current
            );

            //console.log("Initial bounds", [[sw.lat(), sw.lng()], [ne.lat(), ne.lng()]]);
            const boxSlider = document.createElement("div");
            boxSlider.classList.add("slider-container");
            const opacitySlider = document.createElement("input");
            opacitySlider.classList.add("slider");
            opacitySlider.type = "range";
            opacitySlider.min = "0";
            opacitySlider.max = "100";
            opacitySlider.value = "50";
            opacitySlider.addEventListener("change", () => {
                alignerOverlay.updateOpacity(opacitySlider.value / 100);
            });
            boxSlider.appendChild(opacitySlider);

            const box = document.createElement("div");
            const p = document.createElement("p");
            box.classList.add("textbox");
            p.innerHTML = t("external.moverImgMsg");
            p.style.fontSize = "20px";
            p.style.paddingInlineEnd = "4px";
            p.style.paddingInlineStart = "4px";
            box.appendChild(p);

            gmap.current.controls[window.google.maps.ControlPosition.TOP_RIGHT].push(
                boxSlider
            );
            gmap.current.controls[window.google.maps.ControlPosition.TOP_LEFT].push(
                box
            );

            //console.log(alignerOverlay);
        }

        setInit(true);
        gmap.current.addListener(
            "click",
            () => {
                closeInfoWindow();
            },
            { passive: true }
        );
    };

    useEffect(() => {
        const initCall = async () => {
            try {
                if (apiKey) await injectScript(apiKey, language ? language : "en");
                init();
            } catch (error) {
                onLoadFailed ? onLoadFailed(error) : console.error(error);
            }
        };

        initCall();
    }, []);

    useEffect(() => {
        let center = 0;
        const zoom = 2;
        gmap.current && gmap.current.setZoom(zoom);
        if (!initDone || !gmap.current /* || !data */) return;

        if (data && data.position) {
            center = new window.google.maps.LatLng(
                data.position.lat,
                data.position.lng
            );
        }
        if (
            !center &&
      data &&
      data.bounds &&
      data.bounds.southWest &&
      data.bounds.northEast
        ) {
            const northEast = new window.google.maps.LatLng(
                data.bounds.northEast.lat,
                data.bounds.northEast.lng
            );
            const southWest = new window.google.maps.LatLng(
                data.bounds.southWest.lat,
                data.bounds.southWest.lng
            );
            center = new window.google.maps.LatLngBounds(
                southWest,
                northEast
            ).getCenter();
        }

        const nowCenter = gmap.current.getCenter();
        if (center && (!nowCenter || !nowCenter.equals(center)))
            gmap.current.setCenter(center);
        if (data && data.zoom && gmap.current.getZoom() !== data.zoom)
            gmap.current.setZoom(data.zoom);
    }, [data, initDone, farmId]);

    const mustFilterSectors = Array.isArray(filterSectors);
    const mustFilterNodes = Array.isArray(filterNodes);

    const accountFarms = accountId && Array.isArray(data) ? data : null;
    return (
        <div id="google_maps_div" style={{ height: "100%", width: "100%" }}>
            {childrenCommon && (
                <DrawingManager
                    childrenCommon={childrenCommon}
                    edit={edit}
                    hideNodes={hideNodes}
                />
            )}
            {!notShowPolygons &&
      childrenCommon &&
      sectorsCommon &&
      data &&
      data.sectores
                ? Object.keys(data.sectores).map((sector) => {
                    const isTarget =
              mustFilterSectors && filterSectors.includes(sector);
                    const realTarget = isTarget && !alternateView;

                    return (
                        <SectorPolygon
                            key={sector}
                            childrenCommon={childrenCommon}
                            sectorsCommon={sectorsCommon}
                            sector={sector}
                            editable={edit && isTarget}
                            // we cannot highlight the target as that would overwrite a current drawing
                            isHover={!realTarget && hover.sector === sector}
                            isTarget={realTarget}
                            isSecondary={hideSectors || (mustFilterSectors && !isTarget)}
                            alternateView={alternateView}
                            nodeId={nodeId}
                            isImageAligner={isImageAligner}
                        />
                    );
                })
                : null}
            {!notShowPolygons && childrenCommon && unasigned && unasigned.polygons
                ? unasigned.polygons.map((pol, index) => {
                    return (
                        <UnasignedPolygon
                            key={index}
                            dataPol={pol}
                            childrenCommon={childrenCommon}
                            isHover={hover.unasigned == pol.id}
                        />
                    );
                })
                : null}
            {!isImageAligner &&
      !notShowPoints &&
      childrenCommon &&
      nodesCommon &&
      data &&
      data.nodos
                ? Object.keys(data.nodos).map((nodo) => {
                    const isTarget = mustFilterNodes && filterNodes.includes(nodo);
                    const realTarget = isTarget && !alternateView;

                    return (
                        <NodeMarker
                            key={nodo}
                            childrenCommon={childrenCommon}
                            nodesCommon={nodesCommon}
                            nodo={nodo}
                            editable={edit && isTarget}
                            // we cannot highlight the target as that would overwrite a current drawing
                            isHover={!realTarget && hover.nodo === nodo}
                            isTarget={realTarget}
                            isSecondary={hideNodes || (mustFilterNodes && !isTarget)}
                            husoHorario={husoHorario}
                            fechaUltimoDato={fechaUltimoDato}
                            iconBorder={iconBorder}
                        />
                    );
                })
                : null}
            {!notShowPoints && childrenCommon && unasigned && unasigned.points
                ? unasigned.points.map((pt, index) => {
                    return (
                        <UnasignedMarker
                            key={index}
                            dataNode={pt}
                            childrenCommon={childrenCommon}
                            isHover={hover.unasigned == pt.id}
                        />
                    );
                })
                : null}
            {childrenCommon && accountFarms
                ? accountFarms.map((campo) => {
                    if (campo.latitud !== null && campo.longitud !== null) {
                        return (
                            <FarmMarker
                                key={campo.id}
                                campo={campo}
                                childrenCommon={childrenCommon}
                            />
                        );
                    } else return null;
                })
                : null}

            {childrenCommon &&
      data &&
      (sectorsCommon || nodesCommon || accountFarms) ? (
                    <AfterNodesAndSectorsEffect
                        accountFarms={accountFarms}
                        childrenCommon={childrenCommon}
                        nodesCommon={nodesCommon}
                        sectorsCommon={sectorsCommon}
                        filterNodes={filterNodes}
                        filterSectors={filterSectors}
                        hideNodes={hideNodes}
                        hideSectors={hideSectors}
                        edit={edit}
                        noTargeted={alternateView}
                        unasigned={unasigned}
                    />
                ) : null}
        </div>
    );
};

const MapToExport = withName(GoogleMapsFarm, "FarmMap");
export { updateMapInfo, MapToExport as GoogleMapsFarm };
